import clsx from 'clsx';
import React from 'react';
import { Button } from 'react-bootstrap';

import './BayRcDialog.scss';

import BayFloatManagerBase from '../BayFloatManager';

// BayFloatManager is still a `.jsx` module, so TS infers its defaultless props as
// required and flags every call that omits one. Loosen it here -- the real prop
// contract lives in that component.
const BayFloatManager = BayFloatManagerBase as React.ComponentType<any>;

/*
 * Was built on `rc-dialog`. Phase 2 of .plan/feat-overlay-consolidation moved it
 * onto BayFloatManager; `rc-dialog` is out of package.json.
 *
 * THE PROP SURFACE IS UNCHANGED, so all 6 render sites (3 here, 3 in smc) kept
 * working with no edits. The emitted class names are not:
 *
 *   .rc-dialog          -> .bay-float
 *   .rc-dialog-content  -> .bay-float  (they were two nested nodes, now one)
 *   .rc-dialog-header   -> .bay-float-header
 *   .rc-dialog-title    -> .bay-float-title
 *   .rc-dialog-close    -> .bay-float-close
 *   .rc-dialog-body     -> .bay-float-body
 *   .rc-dialog-footer   -> .bay-float-footer
 *   .rc-dialog-mask     -> .bay-float-dim
 *
 * `.bay-rc-dialog` is unchanged and still lands on the panel, so consumer rules
 * scoped to it keep working. The component keeps its name this round too --
 * renaming it means editing smc's call sites and its `.bay-rc-dialog` CSS, which
 * is a separate mechanical change with its own blast radius.
 *
 * Three props are ACCEPTED AND IGNORED rather than removed, so no call site had
 * to change. Each is a stub on purpose; a fake shim would be worse:
 *
 *   getContainer  rc-dialog's render target. The engine always portals. The one
 *                 site that passed `false` (ActAsModal) did so to keep an emotion
 *                 wrapper as its ancestor; those rules moved to
 *                 TopNav/styles/actAsModal.scss and the prop is now inert.
 *   zIndex        the engine uses the named `--bs-zindex-*` scale. The old
 *                 default here was a raw 9999.
 *   deleteMask    was `maskStyle={{ opacity: 1 }}`, i.e. "do not fade the mask".
 *                 The dim is its own layer with its own transition now.
 */

interface DialogFooterProps {
	buttonText?: string;
	submitClass?: string;
	cancelText?: string;
	hideCancel?: boolean;
	hideSubmit?: boolean;
	submitFunc?: () => void;
	footer?: React.ReactNode;
	disabled?: boolean;
	variant?: string;
	onCancel: () => void;
}

interface BayRcDialogProps {
	[key: string]: any;
	children?: React.ReactNode;
	zIndex?: number;
	buttonText?: string;
	submitClass?: string;
	cancelText?: string;
	closeButton?: boolean;
	closeFunc?: () => void;
	maxHeight?: string | number;
	onHideFunc: (_value?: boolean) => void;
	show?: boolean;
	submitFunc?: () => void;
	isConfirm?: boolean;
	variant?: string;
	mask?: boolean;
	title?: string;
	className?: string;
	size?: 'sm' | 'md' | 'lg' | 'xl';
	scrollable?: boolean;
	disabled?: boolean;
	grayBackground?: boolean;
	hideCancel?: boolean;
	hideSubmit?: boolean;
	hideFooter?: boolean;
	fullscreen?: boolean;
	deleteMask?: boolean;
	footer?: React.ReactNode;
	bodyStyle?: React.CSSProperties;
	style?: React.CSSProperties;
	maskClassName?: string;
	/** Forwarded to the engine: raises the BACKDROP, which owns the whole dialog's
	 *  stacking context, to clear an overlay already open. A number works, but prefer
	 *  a token (`"var(--bs-zindex-popover, 1070)"`) -- eslint's no-restricted-syntax
	 *  rejects a raw `zIndex` literal >= 1000. */
	zIndexBackdrop?: number | string;
	getContainer?: boolean | HTMLElement | (() => HTMLElement);
	showHeaderBorder?: boolean;
	bodyProps?: React.HTMLAttributes<HTMLDivElement>;
	closeIcon?: React.ReactNode;
}

// Confirm/cancel row. Defined at module scope, NOT inside the component body --
// a component created in a render body is a new type every render, which
// remounts the real DOM node and drops :hover, focus and transitions with it.
// This was previously declared inside `BayRcDialog`.
const DialogFooter = ({
	buttonText,
	submitClass,
	cancelText,
	hideCancel,
	hideSubmit,
	submitFunc,
	footer,
	disabled,
	variant,
	onCancel,
}: DialogFooterProps) =>
	footer ? (
		<>{footer}</>
	) : (
		<div
			className={`d-flex w-100 ${
				hideCancel || hideSubmit ? 'justify-content-end' : 'justify-content-between'
			}`}
		>
			{!hideCancel && (
				<Button
					className="cancel-button"
					variant="outline-secondary"
					onClick={onCancel}
				>
					{cancelText ? cancelText : 'Cancel'}
				</Button>
			)}
			{!hideSubmit && (
				<Button
					className={submitClass ? `${submitClass} submit-button` : 'submit-button'}
					disabled={disabled}
					variant={variant ? variant : 'primary'}
					onClick={submitFunc ? submitFunc : () => void 0}
				>
					{buttonText ? buttonText : 'Submit'}
				</Button>
			)}
		</div>
	);

const BayRcDialog = (props: BayRcDialogProps) => {
	const {
		children,
		zIndex: _zIndex,
		hideFooter,
		fullscreen,
		closeButton,
		closeFunc,
		onHideFunc,
		show = false,
		mask,
		title,
		size,
		grayBackground,
		maxHeight,
		bodyStyle,
		style: _style,
		maskClassName,
		zIndexBackdrop,
		getContainer: _getContainer,
		className,
		deleteMask: _deleteMask,
		showHeaderBorder,
		bodyProps,
		closeIcon: _closeIcon,
		...rest
	} = props;

	// No width here on purpose. The body used to be pinned to the same pixel
	// value as the dialog, which meant it could not shrink when the dialog did —
	// so a narrow viewport overflowed instead of reflowing. Width belongs to the
	// panel alone; the body simply fills it.
	//
	// FIXED IN PASSING, because the migration removed the mechanism rather than
	// the symptom: `maxHeight` used to go through a template literal, so the `400`
	// fallback reached the DOM as the STRING "400". React only appends `px` to
	// numeric values, so `max-height: 400` was invalid CSS and was dropped — the
	// default max-height had never applied to any dialog in its life.
	//
	// Making that default suddenly bite would clamp and scroll four call sites
	// that render fine today, so the default is NOT reinstated. `maxHeight` now
	// applies only when a caller passes one explicitly. The engine's
	// `.bay-float-body` already scrolls inside a panel capped at `max-height: 100%`,
	// which is what those four have effectively been relying on all along.
	const dialogBodyStyles: React.CSSProperties = {
		...(maxHeight == null ? {} : { maxHeight: fullscreen ? '100%' : maxHeight }),
		...(grayBackground ? { backgroundColor: 'var(--bs-gray-25)' } : {}),
		...bodyStyle,
	};

	const compiledClassNames = clsx(
		'bay-rc-dialog',
		className,
		showHeaderBorder && 'header-divider',
		hideFooter && 'no-footer',
		rest?.hideCancel && rest?.hideSubmit && 'no-buttons',
	);

	return (
		<BayFloatManager
			// `title` is optional here (ActAsModal drops it while loading), and a
			// dialog with no accessible name is announced as just "dialog". The
			// engine only applies this when there is no title to label it with.
			aria-label="Dialog"
			backdrop={mask === false ? false : true}
			backdropClassName={maskClassName}
			bodyClassName={bodyProps?.className}
			bodyStyle={dialogBodyStyles}
			className={compiledClassNames}
			closeButton={closeButton !== false}
			footer={
				!hideFooter ? (
					<DialogFooter {...rest} onCancel={() => onHideFunc(false)} />
				) : undefined
			}
			// `afterClose` -> `onExited`. rc-dialog also took `destroyOnClose`; the
			// engine always unmounts on exit, so there is nothing to opt into.
			onExited={closeFunc}
			// `fullscreen` was a separate boolean that set width:100% on the body.
			// It is a size step on the engine, which also drops the radius and the
			// centring gutter — which is what the four `fullscreen` call sites were
			// hand-rolling with `vw-100 mw-100 vh-100` utility classes.
			size={fullscreen ? 'fullscreen' : size || 'md'}
			title={title}
			show={show}
			zIndexBackdrop={zIndexBackdrop}
			onClose={onHideFunc}
		>
			{children}
		</BayFloatManager>
	);
};

export default BayRcDialog;
